import { useMemo, useState, useEffect } from "react";
import { Package, Trash2 } from "lucide-react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api, ApiError, apiDownload } from "../lib/api";
import { fmtDate, rm } from "../lib/format";
import { toast } from "../stores/toast";
import { useAuth } from "../stores/auth";
import {
  Badge,
  Button,
  Input,
  PageLoader,
  SearchSelect,
  Textarea,
  statusColor,
  useConfirm,
} from "../components/ui";
import { DataTable, type Column } from "../components/DataTable";
import { ListPageShell } from "../components/shell/ListPageShell";
import { flattenInfinitePages, getNextPageParam } from "../lib/infinite-list";
import { showQueryLoader } from "../lib/query-utils";
import { RightSidebarPanel } from "../components/RightSidebarPanel";
import { useQueryRecordId } from "../lib/use-query-record";
import { ManualOrderCreatePanel } from "../components/orders/ManualOrderCreatePanel";
import {
  FULFIL_ORDER_STATUSES,
  marketplaceFulfilmentMethodLabel,
  marketplaceFulfilmentStatusLabel,
  MARKETPLACE_PAYOUT_STATUS_LABELS,
  POST_PAYMENT_ORDER_STATUSES,
  formatPickupAddress,
  orderStatusLabel,
} from "@gaole/shared";

type OrderStatus =
  | "ALL"
  | "PENDING_PAYMENT"
  | "PAID"
  | "PICKED"
  | "PACKED"
  | "SHIPPED"
  | "DELIVERED"
  | "CANCELLED";

const STATUS_OPTIONS: { value: OrderStatus; label: string }[] = [
  { value: "ALL", label: "All" },
  { value: "PENDING_PAYMENT", label: "Confirm" },
  { value: "PAID", label: "Paid" },
  { value: "PICKED", label: "Picked" },
  { value: "PACKED", label: "Packed" },
  { value: "SHIPPED", label: "Shipped" },
  { value: "DELIVERED", label: "Delivered" },
  { value: "CANCELLED", label: "Cancelled" },
];

const KIND_LABEL: Record<string, string> = {
  STORE: "Shop",
  MANUAL: "Manual",
  MARKETPLACE: "Marketplace",
  RAFFLE_WIN: "Raffle win",
  AUCTION_WIN: "Auction win",
  CREDIT_TOPUP: "Credit top-up",
  VAULT_SHIPMENT: "Vault shipment",
};

interface OrderListItem {
  id: string;
  orderNo: string;
  kind: string;
  status: string;
  total: number;
  customer?: string;
  customerEmail?: string;
  trackingNo: string | null;
  courier: string | null;
  fulfilmentMethod?: string;
  createdAt: string;
  items: { id: string; name: string; quantity: number }[];
}

interface OrderPickupDetails {
  locationId: string;
  name: string;
  address1: string;
  address2?: string | null;
  city: string;
  state: string;
  postcode: string;
  country: string;
  phone?: string | null;
  instructions?: string | null;
  scheduledAt: string;
}

interface ShippingAddress {
  name: string;
  phone: string;
  address1: string;
  address2: string | null;
  postcode: string;
  city: string;
  state: string;
  country: string;
}

interface OrderDetail extends OrderListItem {
  subtotal: number;
  discountTotal: number;
  shippingFee: number;
  creditUsed: number;
  sellerFulfilmentStatus?: string | null;
  payoutStatus?: string | null;
  sellerShopName?: string | null;
  sellerNet?: number | null;
  platformFee?: number | null;
  shippingAddress: ShippingAddress | null;
  pickupDetails?: OrderPickupDetails | null;
  paymentDeadline: string | null;
  paidAt: string | null;
  shippedAt: string | null;
  deliveredAt: string | null;
  courier: string | null;
  items: { id: string; productId: string | null; name: string; price: number; discount: number; quantity: number }[];
  paymentIntents: { id: string; method: string; status: string; amount: number; externalId: string | null; createdAt: string }[];
}

const NEXT_STATUS: Record<string, { value: string; label: string }[]> = {
  PAID: [{ value: "PICKED", label: "Mark picked" }],
  PICKED: [{ value: "PACKED", label: "Mark packed" }],
  PACKED: [{ value: "SHIPPED", label: "Mark shipped" }],
  SHIPPED: [{ value: "DELIVERED", label: "Mark delivered" }],
};

function nextStatusOptions(order: OrderDetail): { value: string; label: string }[] {
  if (order.status === "PACKED" && order.fulfilmentMethod === "PICKUP") {
    return [{ value: "DELIVERED", label: "Mark collected" }];
  }
  return NEXT_STATUS[order.status] ?? [];
}

const BULK_STATUS_OPTIONS = FULFIL_ORDER_STATUSES.map((value) => ({
  value,
  label: orderStatusLabel(value),
}));

const PAGE_SIZE = 25;

const PAID_DOC_STATUSES = new Set<string>(POST_PAYMENT_ORDER_STATUSES);

export function Orders() {
  const queryClient = useQueryClient();
  const { can, moduleEnabled, config } = useAuth();
  const collectLabel = config?.marketplaceCollectLabel ?? "COD";
  const { confirm, dialog } = useConfirm();
  const canFulfil = can("orders.fulfil");
  const canWrite = can("orders.write");
  const [searchParams, setSearchParams] = useSearchParams();

  const status = (searchParams.get("status") as OrderStatus | null) ?? "ALL";
  const search = searchParams.get("search") ?? "";

  const [searchInput, setSearchInput] = useState(search);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { recordId, clearRecordId } = useQueryRecordId();
  const [cancelReason, setCancelReason] = useState("");
  const [nextStatus, setNextStatus] = useState("");
  const [trackingNo, setTrackingNo] = useState("");
  const [courier, setCourier] = useState("");
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkTrackingNo, setBulkTrackingNo] = useState("");
  const [bulkCourier, setBulkCourier] = useState("");
  const [removedItemIds, setRemovedItemIds] = useState<Set<string>>(() => new Set());
  const [extendHours, setExtendHours] = useState("24");
  const [createManualOpen, setCreateManualOpen] = useState(false);

  const listQuery = useInfiniteQuery({
    queryKey: ["orders", status, search],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ page: String(pageParam), pageSize: String(PAGE_SIZE) });
      if (status !== "ALL") params.set("status", status);
      if (search.trim()) params.set("search", search.trim());
      return api<{ items: OrderListItem[]; total: number; page: number; pageSize: number }>(
        `/orders?${params}`,
      );
    },
    getNextPageParam: (lastPage, allPages) =>
      getNextPageParam(
        lastPage.total,
        allPages.reduce((acc, page) => acc + page.items.length, 0),
        PAGE_SIZE,
      ),
  });

  const orderRows = flattenInfinitePages(listQuery.data?.pages);
  const listTotal = listQuery.data?.pages[0]?.total ?? 0;
  const hasNextPage = orderRows.length < listTotal && (listQuery.hasNextPage ?? false);

  const detailQuery = useQuery({
    queryKey: ["order", selectedId],
    queryFn: () => api<{ order: OrderDetail }>(`/orders/${selectedId}`),
    enabled: !!selectedId,
  });

  useEffect(() => {
    if (!recordId) return;
    setSelectedId(recordId);
    clearRecordId();
  }, [recordId, clearRecordId]);

  useEffect(() => {
    setRemovedItemIds(new Set());
  }, [selectedId, detailQuery.dataUpdatedAt]);

  const itemsMutation = useMutation({
    mutationFn: (args: { id: string; itemIds: string[] }) =>
      api<{ order: OrderDetail }>(`/orders/${args.id}/items`, {
        method: "PUT",
        body: { itemIds: args.itemIds },
      }),
    onSuccess: () => {
      toast("success", "Line items updated");
      setRemovedItemIds(new Set());
      void queryClient.invalidateQueries({ queryKey: ["order", selectedId] });
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Save failed"),
  });

  const cancelMutation = useMutation({
    mutationFn: (args: { id: string; reason: string }) =>
      api(`/orders/${args.id}/cancel`, { body: { reason: args.reason } }),
    onSuccess: () => {
      toast("success", "Order cancelled");
      setCancelReason("");
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
      void queryClient.invalidateQueries({ queryKey: ["order", selectedId] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Cancel failed"),
  });

  const extendDeadlineMutation = useMutation({
    mutationFn: (args: { id: string; hours: number }) =>
      api<{ order: OrderDetail }>(`/orders/${args.id}/extend-deadline`, {
        method: "POST",
        body: { hours: args.hours },
      }),
    onSuccess: () => {
      toast("success", "Payment deadline extended");
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
      void queryClient.invalidateQueries({ queryKey: ["order", selectedId] });
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Extend failed"),
  });

  const statusMutation = useMutation({
    mutationFn: (args: { id: string; status: string; trackingNo?: string; courier?: string }) =>
      api(`/orders/${args.id}/status`, {
        body: {
          status: args.status,
          ...(args.trackingNo ? { trackingNo: args.trackingNo } : {}),
          ...(args.courier ? { courier: args.courier } : {}),
        },
      }),
    onSuccess: () => {
      toast("success", "Status updated");
      setNextStatus("");
      setTrackingNo("");
      setCourier("");
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
      void queryClient.invalidateQueries({ queryKey: ["order", selectedId] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Update failed"),
  });

  const bulkStatusMutation = useMutation({
    mutationFn: (args: { orderIds: string[]; status: string; trackingNo?: string; courier?: string }) =>
      api<{ updated: number; results: { orderId: string; ok: boolean; error?: string }[] }>(
        "/orders/bulk-status",
        {
          body: {
            orderIds: args.orderIds,
            status: args.status,
            ...(args.trackingNo ? { trackingNo: args.trackingNo } : {}),
            ...(args.courier ? { courier: args.courier } : {}),
          },
        },
      ),
    onSuccess: (data) => {
      const skipped = data.results.filter((r) => !r.ok).length;
      const firstError = data.results.find((r) => !r.ok)?.error;
      if (data.updated === 0) {
        toast("error", firstError ?? "No orders were updated");
      } else if (skipped > 0) {
        toast(
          "info",
          `Updated ${data.updated} order${data.updated === 1 ? "" : "s"} · ${skipped} skipped${firstError ? ` — ${firstError}` : ""}`,
        );
      } else {
        toast("success", `Updated ${data.updated} order${data.updated === 1 ? "" : "s"}`);
      }
      setSelected(new Set());
      setBulkStatus("");
      setBulkTrackingNo("");
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
      void queryClient.invalidateQueries({ queryKey: ["order"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Bulk update failed"),
  });

  const columns: Column<OrderListItem>[] = useMemo(
    () => [
      {
        key: "orderNo",
        header: "Order#",
        sortable: true,
        sortValue: (r) => r.orderNo,
        render: (r) => <span className="font-mono text-xs">{r.orderNo}</span>,
      },
      {
        key: "customer",
        header: "Customer",
        sortable: true,
        sortValue: (r) => r.customer ?? r.customerEmail ?? "",
        exportValue: (r) => [r.customer, r.customerEmail].filter(Boolean).join(" · "),
        render: (r) => (
          <div>
            <p className="text-sm">{r.customer ?? "—"}</p>
            {r.customerEmail && <p className="text-[10px] text-white/40">{r.customerEmail}</p>}
          </div>
        ),
      },
      {
        key: "kind",
        header: "Kind",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.kind,
        exportValue: (r) => KIND_LABEL[r.kind] ?? r.kind,
        render: (r) => <span className="text-xs text-white/60">{KIND_LABEL[r.kind] ?? r.kind}</span>,
      },
      {
        key: "fulfilment",
        header: "Method",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.fulfilmentMethod ?? "DELIVERY",
        render: (r) =>
          r.fulfilmentMethod === "PICKUP" ? (
            <Badge color="blue">Pickup</Badge>
          ) : r.fulfilmentMethod === "VAULT" ? (
            <Badge color="pink">Vault</Badge>
          ) : (
            <span className="text-xs text-muted">Delivery</span>
          ),
      },
      {
        key: "status",
        header: "Status",
        sortable: true,
        sortValue: (r) => r.status,
        exportValue: (r) => r.status,
        render: (r) => <Badge color={statusColor(r.status)}>{orderStatusLabel(r.status)}</Badge>,
      },
      {
        key: "total",
        header: "Total",
        className: "text-right",
        sortable: true,
        sortValue: (r) => r.total,
        exportValue: (r) => r.total,
        render: (r) => <span className="font-display font-semibold">{rm(r.total)}</span>,
      },
      {
        key: "created",
        header: "Created",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.createdAt,
        exportValue: (r) => fmtDate(r.createdAt),
        render: (r) => <span className="text-xs text-white/50">{fmtDate(r.createdAt)}</span>,
      },
    ],
    [],
  );

  function updateParams(patch: Record<string, string | null>) {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value == null || value === "") next.delete(key);
      else next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  }

  function openOrder(row: OrderListItem) {
    setSelectedId(row.id);
    setCancelReason("");
    setExtendHours("24");
    setNextStatus("");
    setTrackingNo(row.trackingNo ?? "");
    setCourier(row.courier ?? "");
    setRemovedItemIds(new Set());
  }

  const order = detailQuery.data?.order;

  useEffect(() => {
    if (!order) return;
    setTrackingNo(order.trackingNo ?? "");
    setCourier(order.courier ?? "");
  }, [order?.id, order?.trackingNo, order?.courier]);
  const paymentDeadlineMs = order?.paymentDeadline ? new Date(order.paymentDeadline).getTime() : null;
  const paymentDeadlineExpired = paymentDeadlineMs != null && paymentDeadlineMs <= Date.now();
  const canExtendDeadline = Boolean(
    order && order.status === "PENDING_PAYMENT" && canWrite && (order.paymentDeadline || order.kind === "RAFFLE_WIN" || order.kind === "AUCTION_WIN"),
  );
  const statusOptions = order ? nextStatusOptions(order) : [];
  const isPickupOrder = order?.fulfilmentMethod === "PICKUP";
  const canEditItems = Boolean(order && order.status === "PENDING_PAYMENT" && canWrite);
  const visibleItems = order?.items.filter((item) => !removedItemIds.has(item.id)) ?? [];
  const itemsDirty = removedItemIds.size > 0;

  const draftTotal = useMemo(() => {
    if (!order || !itemsDirty) return order?.total ?? 0;
    const subtotal = visibleItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const discount = visibleItems.reduce((sum, item) => sum + item.discount, 0);
    const merchandise = subtotal - discount;
    const shipping = order.fulfilmentMethod === "PICKUP" ? 0 : order.shippingFee;
    return Math.max(0, merchandise + shipping);
  }, [order, visibleItems, itemsDirty]);

  const orderSelection = useMemo(() => {
    if (!canFulfil) return undefined;
    return {
      entityLabel: "Order",
      trailing: (
        <div className="flex flex-wrap items-center gap-2">
          <SearchSelect
            className="w-36"
            placeholder="Status…"
            options={BULK_STATUS_OPTIONS}
            value={bulkStatus}
            onChange={setBulkStatus}
          />
          {bulkStatus === "SHIPPED" && (
            <>
              <Input
                className="w-44"
                placeholder="Tracking no."
                value={bulkTrackingNo}
                onChange={(e) => setBulkTrackingNo(e.target.value)}
                aria-label="Tracking number"
              />
              <Input
                className="w-36"
                placeholder="Courier"
                value={bulkCourier}
                onChange={(e) => setBulkCourier(e.target.value)}
                aria-label="Courier or provider"
              />
            </>
          )}
          <Button
            size="sm"
            disabled={!bulkStatus || bulkStatusMutation.isPending}
            onClick={() => void applyBulkStatus()}
          >
            Update
          </Button>
        </div>
      ),
    };
  }, [canFulfil, bulkStatus, bulkTrackingNo, bulkCourier, bulkStatusMutation.isPending, selected]);

  function applyBulkStatus() {
    if (!bulkStatus || selected.size === 0) return;

    const rows = orderRows;
    const selectedRows = rows.filter((r: OrderListItem) => selected.has(r.id));
    if (bulkStatus === "SHIPPED") {
      const needsTracking = selectedRows.some((r: OrderListItem) => !r.trackingNo);
      if (needsTracking && !bulkTrackingNo.trim()) {
        toast("error", "Add a tracking number for orders without one");
        return;
      }
    }

    const orderIds = [...selected];
    const statusLabel = BULK_STATUS_OPTIONS.find((o) => o.value === bulkStatus)?.label ?? bulkStatus;

    const run = () =>
      bulkStatusMutation.mutate({
        orderIds,
        status: bulkStatus,
        trackingNo: bulkTrackingNo.trim() || undefined,
        courier: bulkCourier.trim() || undefined,
      });

    if (selected.size >= 5 || bulkStatus === "SHIPPED" || bulkStatus === "DELIVERED") {
      confirm({
        title: "Update selected orders?",
        message: `Move ${selected.size} order${selected.size === 1 ? "" : "s"} to ${statusLabel}.`,
        confirmLabel: "Update",
        action: run,
      });
      return;
    }

    run();
  }

  return (
    <ListPageShell>
      <DataTable
        columns={columns}
        rows={orderRows}
        rowKey={(r) => r.id}
        onRowClick={openOrder}
        activeKey={selectedId}
        loading={showQueryLoader(listQuery)}
        exportFilename="orders"
        entityLabel="Order"
        selectable={canFulfil}
        selectedKeys={canFulfil ? selected : undefined}
        onSelectedKeysChange={canFulfil ? setSelected : undefined}
        selection={orderSelection}
        infiniteScroll={{
          hasNextPage,
          isFetchingNextPage: listQuery.isFetchingNextPage,
          onLoadMore: () => void listQuery.fetchNextPage(),
        }}
        toolbar={{
          search: {
            value: searchInput,
            onChange: setSearchInput,
            onSubmit: () => updateParams({ search: searchInput.trim() || null }),
            placeholder: "Search order#, customer, tracking…",
          },
          views: {
            options: STATUS_OPTIONS,
            value: status,
            onChange: (v) => updateParams({ status: v === "ALL" ? null : v }),
          },
          trailing: canWrite ? (
            <Button size="sm" onClick={() => setCreateManualOpen(true)}>
              Create
            </Button>
          ) : undefined,
          onRefresh: () => void listQuery.refetch(),
        }}
        empty={{
          icon: Package,
          title: "No orders found",
          hint: search || status !== "ALL" ? "Try clearing filters." : undefined,
        }}
      />

      <ManualOrderCreatePanel
        open={createManualOpen}
        onClose={() => setCreateManualOpen(false)}
        onCreated={(id) => {
          setCreateManualOpen(false);
          setSelectedId(id);
          void queryClient.invalidateQueries({ queryKey: ["orders"] });
        }}
      />

      <RightSidebarPanel
        open={!!selectedId}
        title={order?.orderNo ?? "Order"}
        subtitle={order ? `${KIND_LABEL[order.kind] ?? order.kind} · ${order.customer ?? ""}` : undefined}
        wide
        onClose={() => {
          setSelectedId(null);
          setRemovedItemIds(new Set());
          setCancelReason("");
        }}
        footer={
          order && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Button variant="ghost" size="sm" onClick={() => setSelectedId(null)}>
                Cancel
              </Button>
              <div className="flex flex-wrap justify-end gap-2">
                {canEditItems && itemsDirty && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={itemsMutation.isPending}
                      onClick={() => setRemovedItemIds(new Set())}
                    >
                      Reset
                    </Button>
                    <Button
                      size="sm"
                      disabled={itemsMutation.isPending || visibleItems.length === 0}
                      onClick={() =>
                        itemsMutation.mutate({
                          id: order.id,
                          itemIds: visibleItems.map((item) => item.id),
                        })
                      }
                    >
                      Save
                    </Button>
                  </>
                )}
                {canFulfil && statusOptions.length > 0 && (
                  <Button
                    size="sm"
                    disabled={!nextStatus || statusMutation.isPending}
                    onClick={() => {
                      if (nextStatus === "SHIPPED" && !isPickupOrder && !trackingNo.trim() && !order.trackingNo) {
                        toast("error", "Add a tracking number before shipping");
                        return;
                      }
                      statusMutation.mutate({
                        id: order.id,
                        status: nextStatus,
                        trackingNo: trackingNo.trim() || undefined,
                        courier: courier.trim() || undefined,
                      });
                    }}
                  >
                    Update status
                  </Button>
                )}
                {can("documents.read") && PAID_DOC_STATUSES.has(order.status) && (
                  <>
                    {moduleEnabled("invoicing") && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          void apiDownload(
                            `/orders/${order.id}/documents/INVOICE`,
                            `invoice-${order.orderNo}.pdf`,
                          ).catch((e) => toast("error", e instanceof ApiError ? e.message : "Download failed"))
                        }
                      >
                        Invoice
                      </Button>
                    )}
                    {moduleEnabled("documents") && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          void apiDownload(
                            `/orders/${order.id}/documents/RECEIPT`,
                            `receipt-${order.orderNo}.pdf`,
                          ).catch((e) => toast("error", e instanceof ApiError ? e.message : "Download failed"))
                        }
                      >
                        Receipt
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          )
        }
      >
        {detailQuery.isLoading ? (
          <PageLoader />
        ) : order ? (
          <div className="space-y-5 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <Badge color={statusColor(order.status)}>{orderStatusLabel(order.status)}</Badge>
              {isPickupOrder && (
                <Badge color="blue">
                  {order.kind === "MARKETPLACE" ? marketplaceFulfilmentMethodLabel("PICKUP", collectLabel) : "Pickup"}
                </Badge>
              )}
              {order.fulfilmentMethod === "VAULT" && <Badge color="pink">Stored in vault</Badge>}
              {order.kind === "MARKETPLACE" && order.sellerFulfilmentStatus && (
                <Badge color="amber">{marketplaceFulfilmentStatusLabel(order.sellerFulfilmentStatus, collectLabel)}</Badge>
              )}
              {order.kind === "MARKETPLACE" && order.payoutStatus && (
                <Badge color={statusColor(order.payoutStatus === "SETTLED" ? "DELIVERED" : "PENDING")}>
                  {MARKETPLACE_PAYOUT_STATUS_LABELS[order.payoutStatus] ?? order.payoutStatus}
                </Badge>
              )}
              <span className="font-display text-lg font-bold text-brand-pink">{rm(order.total)}</span>
              {order.creditUsed > 0 && (
                <span className="text-xs text-white/50">({order.creditUsed} credits used)</span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <p className="text-white/40">Created</p>
                <p>{fmtDate(order.createdAt)}</p>
              </div>
              {order.paidAt && (
                <div>
                  <p className="text-white/40">Paid</p>
                  <p>{fmtDate(order.paidAt)}</p>
                </div>
              )}
              {order.paymentDeadline && (
                <div>
                  <p className="text-white/40">Pay by</p>
                  <p className={paymentDeadlineExpired ? "text-red-300" : undefined}>{fmtDate(order.paymentDeadline)}</p>
                </div>
              )}
              {order.trackingNo && (
                <div className="col-span-2">
                  <p className="text-white/40">Tracking</p>
                  <p className="font-mono">
                    {order.trackingNo}
                    {order.courier ? ` · ${order.courier}` : ""}
                  </p>
                </div>
              )}
              {order.kind === "MARKETPLACE" && order.sellerShopName && (
                <div className="col-span-2">
                  <p className="text-white/40">Seller shop</p>
                  <p>{order.sellerShopName}</p>
                </div>
              )}
              {order.kind === "MARKETPLACE" && order.sellerNet != null && (
                <div>
                  <p className="text-white/40">Seller net</p>
                  <p>{rm(order.sellerNet)}</p>
                </div>
              )}
            </div>

            <section>
              <div className="mb-2 flex items-center justify-between gap-2">
                <h4 className="font-display text-xs font-semibold uppercase tracking-wider text-white/40">
                  Line items
                </h4>
                {canEditItems && (
                  <span className="text-[10px] text-white/40">Remove rows, then Save</span>
                )}
              </div>
              <ul className="space-y-2">
                {visibleItems.map((item) => (
                  <li
                    key={item.id}
                    className="flex items-center justify-between gap-2 rounded-xl border border-line bg-white/[0.02] px-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{item.name}</p>
                      <p className="font-display text-xs font-semibold tabular-nums text-muted">Qty {item.quantity}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="font-display font-semibold">
                        {rm(item.price * item.quantity - item.discount)}
                      </span>
                      {canEditItems && visibleItems.length > 1 && (
                        <button
                          type="button"
                          className="rounded-lg p-1.5 text-white/40 transition-colors hover:bg-red-500/10 hover:text-red-400"
                          aria-label={`Remove ${item.name}`}
                          onClick={() =>
                            setRemovedItemIds((prev) => {
                              const next = new Set(prev);
                              next.add(item.id);
                              return next;
                            })
                          }
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              {itemsDirty && (
                <p className="mt-2 text-xs text-white/50">
                  New total after save:{" "}
                  <span className="font-display font-semibold text-brand-pink">{rm(draftTotal)}</span>
                  {order.fulfilmentMethod !== "PICKUP" && order.shippingFee > 0 && (
                    <span className="text-white/40"> · shipping may recalculate</span>
                  )}
                </p>
              )}
            </section>

            {order.pickupDetails && (
              <section>
                <h4 className="mb-2 font-display text-xs font-semibold uppercase tracking-wider text-white/40">
                  Store pickup
                </h4>
                <div className="rounded-xl border border-line bg-white/[0.02] px-3 py-2 text-xs leading-relaxed">
                  <p className="font-medium">{order.pickupDetails.name}</p>
                  {order.pickupDetails.phone && (
                    <p className="text-white/60">{order.pickupDetails.phone}</p>
                  )}
                  <p className="mt-1">{formatPickupAddress(order.pickupDetails)}</p>
                  {order.pickupDetails.instructions && (
                    <p className="mt-1 text-white/60">{order.pickupDetails.instructions}</p>
                  )}
                  <p className="mt-2 font-medium text-brand-pink">
                    Scheduled: {fmtDate(order.pickupDetails.scheduledAt)}
                  </p>
                </div>
              </section>
            )}

            {order.shippingAddress && (
              <section>
                <h4 className="mb-2 font-display text-xs font-semibold uppercase tracking-wider text-white/40">
                  Shipping address
                </h4>
                <div className="rounded-xl border border-line bg-white/[0.02] px-3 py-2 text-xs leading-relaxed">
                  <p className="font-medium">{order.shippingAddress.name}</p>
                  <p className="text-white/60">{order.shippingAddress.phone}</p>
                  <p className="mt-1">
                    {order.shippingAddress.address1}
                    {order.shippingAddress.address2 ? `, ${order.shippingAddress.address2}` : ""}
                  </p>
                  <p>
                    {order.shippingAddress.postcode} {order.shippingAddress.city},{" "}
                    {order.shippingAddress.state}
                  </p>
                  <p>{order.shippingAddress.country}</p>
                </div>
              </section>
            )}

            {order.paymentIntents.length > 0 && (
              <section>
                <h4 className="mb-2 font-display text-xs font-semibold uppercase tracking-wider text-white/40">
                  Payment intents
                </h4>
                <ul className="space-y-2">
                  {order.paymentIntents.map((p) => (
                    <li
                      key={p.id}
                      className="flex items-center justify-between gap-2 rounded-xl border border-line bg-white/[0.02] px-3 py-2 text-xs"
                    >
                      <div>
                        <p className="font-medium">{p.method}</p>
                        <p className="text-white/40">{fmtDate(p.createdAt)}</p>
                      </div>
                      <div className="text-right">
                        <Badge color={statusColor(p.status)}>{p.status}</Badge>
                        <p className="mt-0.5">{rm(p.amount)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {canExtendDeadline && (
              <section className="space-y-3 rounded-xl border border-line bg-white/[0.02] p-3">
                <div>
                  <h4 className="font-display text-xs font-semibold uppercase tracking-wider text-white/40">
                    Payment deadline
                  </h4>
                  <p className="mt-1 text-xs text-white/50">
                    Adds time for the same winner to pay. Prize stays reserved — no stock check needed.
                  </p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {[24, 48, 72].map((hours) => (
                    <Button
                      key={hours}
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={extendDeadlineMutation.isPending}
                      onClick={() => setExtendHours(String(hours))}
                      className={extendHours === String(hours) ? "border-brand-pink/50 bg-brand-pink/10" : undefined}
                    >
                      +{hours}h
                    </Button>
                  ))}
                </div>
                <Input
                  label="Extend by (hours)"
                  type="number"
                  min={1}
                  max={720}
                  value={extendHours}
                  onChange={(e) => setExtendHours(e.target.value)}
                  hint={
                    order.paymentDeadline
                      ? paymentDeadlineExpired
                        ? `Expired ${fmtDate(order.paymentDeadline)} — extension starts from now`
                        : `Current deadline: ${fmtDate(order.paymentDeadline)}`
                      : "No deadline set yet — extension starts from now"
                  }
                />
                <Button
                  size="sm"
                  className="w-full"
                  disabled={
                    extendDeadlineMutation.isPending ||
                    !extendHours.trim() ||
                    Number.parseInt(extendHours, 10) < 1 ||
                    Number.parseInt(extendHours, 10) > 720
                  }
                  onClick={() =>
                    confirm({
                      title: "Extend payment deadline?",
                      message: `Give the customer ${extendHours} more hour${extendHours === "1" ? "" : "s"} to pay ${order.orderNo}.`,
                      confirmLabel: "Extend",
                      action: () =>
                        extendDeadlineMutation.mutateAsync({
                          id: order.id,
                          hours: Number.parseInt(extendHours, 10),
                        }),
                    })
                  }
                >
                  Extend deadline
                </Button>
              </section>
            )}

            {order.status === "PENDING_PAYMENT" && canWrite && (
              <section className="space-y-3 rounded-xl border border-line bg-white/[0.02] p-3">
                <Textarea
                  label="Cancel reason"
                  placeholder="Why is this order being cancelled?"
                  rows={3}
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  hint={
                    order.kind === "RAFFLE_WIN" || order.kind === "AUCTION_WIN"
                      ? "Forfeits the win — may re-draw (single raffle) or release the prize hold."
                      : cancelReason.trim().length > 0 && cancelReason.trim().length < 3
                        ? "At least 3 characters"
                        : undefined
                  }
                />
                <Button
                  variant="danger"
                  size="sm"
                  className="w-full"
                  disabled={cancelMutation.isPending || cancelReason.trim().length < 3}
                  onClick={() =>
                    confirm({
                      title: "Cancel order?",
                      message: `Cancel ${order.orderNo} — this cannot be undone.`,
                      confirmLabel: "Cancel order",
                      danger: true,
                      action: () => cancelMutation.mutate({ id: order.id, reason: cancelReason.trim() }),
                    })
                  }
                >
                  Cancel order
                </Button>
              </section>
            )}

            {canFulfil && statusOptions.length > 0 && (
              <div className="space-y-3 rounded-xl border border-line bg-white/[0.02] p-3">
                <SearchSelect
                  label="Next status"
                  placeholder="Choose…"
                  options={[{ value: "", label: "Choose…" }, ...statusOptions]}
                  value={nextStatus}
                  onChange={setNextStatus}
                />
                {(nextStatus === "SHIPPED" || (order.status === "PACKED" && !isPickupOrder)) && (
                  <>
                    <Input
                      label="Tracking number"
                      placeholder="EasyParcel / courier tracking no."
                      value={trackingNo}
                      onChange={(e) => setTrackingNo(e.target.value)}
                    />
                    <Input
                      label="Courier / provider"
                      placeholder="Pos Laju, J&T…"
                      value={courier}
                      onChange={(e) => setCourier(e.target.value)}
                    />
                  </>
                )}
              </div>
            )}
          </div>
        ) : null}
      </RightSidebarPanel>

      {dialog}
    </ListPageShell>
  );
}
